import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { LcPoolSummary } from './useLiquidityCachePools';

interface UseLcPoolReturn {
  pool: LcPoolSummary | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLiquidityCachePool(poolId: string | undefined): UseLcPoolReturn {
  const [pool, setPool] = useState<LcPoolSummary | null>(null);
  const [loading, setLoading] = useState(!!poolId);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchPool = useCallback(async () => {
    if (!poolId) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getLiquidityCachePool(poolId);
      if (fetchIdRef.current !== id) return;
      setPool(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load pool');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [poolId]);

  const refresh = useCallback(() => {
    setError(null);
    fetchPool();
  }, [fetchPool]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { pool, loading, error, refresh };
}
